import { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import AdminSidebar from "../../components/admin/AdminSidebar"
import AdminHeader from "../../components/admin/AdminHeader"
import { useWebinars } from "../../hooks/useWebinars"
import { Button } from "../../components/ui/button"
import Card, { CardContent, CardHeader, CardTitle } from "../../components/Card"
import { ArrowLeft } from "lucide-react"
import { formatDate, formatTime } from "../../utils/formatters"

export default function WebinarRegistrationsPage() {
  const { webinarId } = useParams()
  const navigate = useNavigate()
  const { getWebinar, getWebinarRegistrations, loading } = useWebinars()
  const [webinar, setWebinar] = useState(null)
  const [registrations, setRegistrations] = useState([])
  const [loadingRegistrations, setLoadingRegistrations] = useState(true)
  const [page, setPage] = useState(1)
  const [limit] = useState(20)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)

  useEffect(() => {
    const loadData = async () => {
      setLoadingRegistrations(true)
      try {
        const [webinarData, registrationsData] = await Promise.all([
          getWebinar(webinarId),
          getWebinarRegistrations(webinarId, { page, limit })
        ])
        setWebinar(webinarData)
        // L'API retourne { data: [...], total, page, limit, totalPages }
        setRegistrations(registrationsData?.data || [])
        setTotal(registrationsData?.total || 0)
        setTotalPages(registrationsData?.totalPages || 1)
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error)
      } finally {
        setLoadingRegistrations(false)
      }
    }
    loadData()
  }, [webinarId, page, limit])

  return (
    <div className="flex h-screen bg-gray-50">
      <AdminSidebar />
      <div className="flex-1 ml-64 p-8 overflow-y-auto">
        <AdminHeader title="Inscriptions au webinaire" />
        
        <Button
          variant="ghost"
          onClick={() => navigate("/admin/webinars")}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>

        {loadingRegistrations ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#141835] mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des inscriptions...</p>
          </div>
        ) : (
          <>
            {/* Informations du webinaire */}
            {webinar && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>{webinar.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-2">
                    {webinar.description}
                  </p>
                  <p className="text-sm text-gray-500">
                    Total: {total} inscription{total !== 1 ? 's' : ''}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Liste des inscriptions */}
            <Card>
              <CardHeader>
                <CardTitle>Liste des inscrits</CardTitle>
              </CardHeader>
              <CardContent>
                {registrations.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">Aucune inscription pour ce webinaire</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Nom
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Téléphone
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date d'inscription
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {registrations.map((registration) => {
                          const registeredDate = registration.registeredAt
                            ? new Date(registration.registeredAt)
                            : null
                          const user = registration.user || {}
                          return (
                            <tr key={registration.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {user.name || "N/A"}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {user.email || "N/A"}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {user.phone || "N/A"}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {registeredDate ? (
                                  <div className="text-sm text-gray-900">
                                    {formatDate(registeredDate)}
                                    <br />
                                    <span className="text-gray-500">{formatTime(registeredDate)}</span>
                                  </div>
                                ) : (
                                  <span className="text-sm text-gray-500">N/A</span>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-6 flex justify-center items-center gap-4">
                    <Button
                      variant="outline"
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1 || loadingRegistrations}
                    >
                      Précédent
                    </Button>
                    <span className="text-sm text-gray-600">
                      Page {page} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages || loadingRegistrations}
                    >
                      Suivant
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  )
}

